import React from 'react';
import PropTypes from 'prop-types';
import {MultiSelectCheckbox} from '../SharedComponents/MultiSelectCheckbox';
import {QuestionFooter} from '../SharedComponents/QuestionFooter';
import {QUESTION_CONTENTS} from '@learningblocks/exp-constants/lib/Utilities';
import {Question} from '../SharedComponents/Question';
import {MultiSelectFeedback} from '../SharedComponents/MultiSelectFeedback';
import {FeedbackMessage} from '../SharedComponents/FeedbackMessage';
import '../SharedComponents/CommonStyles';

/**
 * Experimental-Design Question component [Multi-Select]:
 *  - You will have n choices to select from
 *  - Your question will have m correct otions
 *  - m will be less than n.
 */
export function MultiSelect(props) {
  const hasUnsubmittedOptions = () => {
    return props.checkboxes.reduce((boolVal, checkboxOption) => {
      if (
        checkboxOption.disabled === false &&
        checkboxOption.selected === true
      ) {
        boolVal = true;
      }
      return boolVal;
    }, false);
  };

  const setSubmitDisabled = () => {
    if (props.complete) return true;
    else return hasUnsubmittedOptions() === false;
  };

  const getIsCorrect = ({id}) => props.answerId.includes(id);

  const renderCheckboxOptions = (key) => {
    return props.checkboxes.map((option, optionID) => (
      <MultiSelectCheckbox
        key={`${key}_${optionID}`}
        className={`${props.className}__option`}
        componentID={props.id}
        isCorrect={getIsCorrect(option)}
        selectOption={props.selectOption}
        {...option}
      />
    ));
  };

  const renderQuestion = (key) => (
    <Question
      key={key}
      className={`${props.className}__text`}
      question={props.question}
    />
  );

  const renderByType = (type, key) => {
    switch (type) {
      case QUESTION_CONTENTS.QUESTION:
        return renderQuestion(key);
      case QUESTION_CONTENTS.CHECKBOXES:
        return renderCheckboxOptions(key);
      default:
        return null;
    }
  };

  const renderContents = () => props.content.map(renderByType);

  return (
    <div className={props.className}>
      {renderContents()}
      <MultiSelectFeedback
        answerId={props.answerId}
        checkboxes={props.checkboxes}>
        <FeedbackMessage className={`${props.className}__feedback`} />
      </MultiSelectFeedback>
      <QuestionFooter
        className={`${props.className}__footer`}
        complete={props.complete}
        componentID={props.id}
        componentType={props.type}
        totalAttempts={props.totalAttempts}
        submitAnswer={props.submitAnswer}
        submitDisabled={setSubmitDisabled()}
      />
    </div>
  );
}

MultiSelect.propTypes = {
  /**
   * The list of ID's for the correct Options
   */
  answerId: PropTypes.arrayOf(PropTypes.number).isRequired,
  /**
   * The list of options.
   */
  checkboxes: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * The custom classname you wanna inject on the root of the component
   */
  className: PropTypes.oneOfType([
    PropTypes.oneOf(['question']),
    PropTypes.string,
  ]).isRequired,
  /**
   * The prop to set if the component is complete
   */
  complete: PropTypes.bool.isRequired,
  /**
   * The prop to specify all contents in the question
   */
  content: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * The prop that hold the unique id for the component
   */
  id: PropTypes.number || PropTypes.string,
  /**
   * The prop that holds the text for the question
   */
  question: PropTypes.string.isRequired,
  /**
   * The prop that holds the total attempts,
   */
  totalAttempts: PropTypes.number.isRequired,
  /**
   * The prop that holds the Component type.
   */
  type: PropTypes.oneOf(['MSCQ']),
  /**
   * The callback prop executed when submit is clicked
   */
  submitAnswer: PropTypes.func.isRequired,
  /**
   * The callback to be executed when an option is selected
   */
  selectOption: PropTypes.func.isRequired,
};

MultiSelect.defaultProps = {
  className: 'question',
  type: 'MSCQ',
  checkboxes: [],
  complete: false,
  content: [],
  question: '',
  totalAttempts: 0,
  answerId: [],
};
